import { InformationCircleIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { useBookingServicePricingAPI } from "@/app/hooks/api_hooks/bookings/useBookingServicePricingAPI";
import { useUpdatePetWeightAPI } from "@/app/hooks/api_hooks/bookings/useUpdatePetWeightAPI";
import { IBookingCartPreviewBillingDetails,IOfferCallout , IBookingFollowUpChipDetails } from "@/api_framework/api_modals/Bookings";
import editicon from "@/assets/images/edit.svg";
import clinicIcon from "@/assets/images/clinic.svg";
import { FaLocationDot } from "react-icons/fa6";
import OfferCalloutBadge from "@/app/components/ui/Badges/OfferCalloutBadge";
import travelIcon from "@/assets/images/travel.svg";
import Species from "@/app/components/ui/Reusable/Species";
import toast from "react-hot-toast";

interface IServiceSummaryCardProps {
  isLoading: boolean;
  upgradeTitle: string;
  searchNode: ReactNode;
  closeNode?: ReactNode;
  customerName: string;
  petName: string;
  pet_species_type_id: string;
  petWeightLabel: string;
  serviceName: string;
  serviceTypeName: string;
  serviceOfferCallout?: IOfferCallout | null;
  servicePriceLabel: string;
  serviceDiscount: number;
  serviceOriginalPriceLabel: string;
  slotStartTimeLabel: string;
  bookingDateLabel: string;
  staffName: string;
  staffExperience: string;
  waivedOffLabel: string;
  chipData?: IBookingCartPreviewBillingDetails | null;
  followUpChip?: IBookingFollowUpChipDetails | null;
  subServiceId?: string | null;
  clinicId?: string | null;
  userId?: string | null;
  petId?: string | null;
  couponId?: string | null;
  // Visit time for surge (slot id, or an overbooking's own start/end).
  surgeTime?: { slot_id?: string; surge_start?: string; surge_end?: string };
  isHomeService?: boolean;
  clinicName?: string;
  visitAddress?: string;
  travelTimeLabel?: string;
  onClickEditVisitAddress?: () => void;
  isPaidBooking?: boolean;
  bookingFee?: number | string | null;
  onPetWeightSaved?: () => Promise<void> | void;
}

const skeletonClass = "animate-pulse rounded bg-[#E9ECF2]";
const normalizePetWeightValue = (value: string) => {
  const nextValue = (value || "").replace(/\s*kgs?\s*$/i, "").trim();
  if (!nextValue || nextValue.toUpperCase() === "NA") return "";
  return nextValue;
};

const ServiceSummaryCard: React.FC<IServiceSummaryCardProps> = ({
  isLoading,
  upgradeTitle,
  searchNode,
  closeNode,
  customerName,
  petName,
  pet_species_type_id,
  petWeightLabel,
  serviceName,
  serviceTypeName,
  serviceOfferCallout,
  servicePriceLabel,
  serviceDiscount,
  serviceOriginalPriceLabel,
  slotStartTimeLabel,
  bookingDateLabel,
  staffName,
  staffExperience,
  waivedOffLabel,
  chipData,
  followUpChip,
  subServiceId,
  clinicId,
  userId,
  petId,
  couponId,
  surgeTime,
  isHomeService = false,
  clinicName = "NA",
  visitAddress = "NA",
  travelTimeLabel = "--",
  onClickEditVisitAddress,
  isPaidBooking = false,
  bookingFee = null,
  onPetWeightSaved,
}) => {
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [currentPetWeightLabel, setCurrentPetWeightLabel] = useState(petWeightLabel || "");
  const [petWeightInput, setPetWeightInput] = useState(normalizePetWeightValue(petWeightLabel || ""));
  const [isPetWeightEditing, setIsPetWeightEditing] = useState(false);
  const [showPetWeightEditorInModal, setShowPetWeightEditorInModal] = useState(false);
  const petWeightInputRef = useRef<HTMLInputElement | null>(null);
  const { execute: fetchServicePricing, isServicePricingInProgress, servicePricingData } = useBookingServicePricingAPI();
  const { execute: updatePetWeight, isPetWeightUpdateInProgress } = useUpdatePetWeightAPI();
  const canFetchPricing = Boolean(subServiceId && clinicId && userId && petId);
  const canUpdatePetWeight = Boolean(userId && petId);
  const openPricingModal = (showPetWeightEditor = false) => {
    setShowPetWeightEditorInModal(showPetWeightEditor);
    if (!showPetWeightEditor) {
      setIsPetWeightEditing(false);
    }
    setIsPricingModalOpen(true);
    if (!canFetchPricing) return;
    fetchServicePricing({
      sub_service_id: String(subServiceId),
      clinic_id: String(clinicId),
      user_id: String(userId),
      pet_id: String(petId),
      ...(couponId ? { coupon_id: String(couponId) } : {}),
      ...surgeTime,
    });
  };

  const formatAmount = (amount: number) => `₹${Number(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  const currentPricing = servicePricingData?.current;
  const sizePricing = servicePricingData?.size_pricing;
  const headerKey = servicePricingData?.header_key
  const headerVal = servicePricingData?.header_val
  const hasServiceDiscount = serviceDiscount > 0;
  const isFreeFollowUp = followUpChip?.price_without_follow_up != null;
  const followUpLabel = [followUpChip?.text, followUpChip?.due_date].filter(Boolean).join(" · ");
  const currentPriceValue = Number(currentPricing?.price ?? 0);
  const currentOriginalPriceValue = Number(currentPricing?.mrp ?? 0);
  const currentDiscountPercent = currentPricing?.discount_percent ?? 0;
  const hasCurrentDiscount = currentDiscountPercent > 0;
  const currentDurationLabel = currentPricing?.duration_mins ? `${currentPricing.duration_mins} mins` : "--";
  const modalTitle = currentPricing?.name || serviceName;
  const normalizedPetWeightDisplay = currentPetWeightLabel?.trim() || "NA";
  const showHomeServiceMeta = isHomeService;
  const bookingFeeLabel = bookingFee === null || bookingFee === undefined || bookingFee === "" ? "₹0" : `₹${bookingFee}`;
  const isVaccination = serviceTypeName?.toLowerCase() === "vaccination" || serviceName?.toLowerCase() === "vaccination";

  useEffect(() => {
    setCurrentPetWeightLabel(petWeightLabel || "");
    setPetWeightInput(normalizePetWeightValue(petWeightLabel || ""));
    setIsPetWeightEditing(false);
  }, [petWeightLabel]);

  const onEditPetWeight = () => {
    setIsPetWeightEditing(true);
  };

  const onClickUpdatePetWeight = () => {
    setPetWeightInput(normalizePetWeightValue(currentPetWeightLabel || ""));
    setIsPetWeightEditing(true);
    openPricingModal(true);
  };

  const onSavePetWeight = async () => {
    const nextValue = normalizePetWeightValue(petWeightInput);
    const numericWeight = Number(nextValue);

    if (!nextValue || Number.isNaN(numericWeight) || numericWeight <= 0) {
      toast.error("Please enter a valid pet weight.");
      return;
    }
    const isUpdated = await updatePetWeight(String(userId), String(petId), nextValue);
    if (!isUpdated) return;
    setCurrentPetWeightLabel(`${nextValue} KG`);
    setPetWeightInput(nextValue);
    setIsPetWeightEditing(false);
    setIsPricingModalOpen(false)
    await onPetWeightSaved?.();
  };

  useEffect(() => {
    if (isPricingModalOpen && isPetWeightEditing) {
      petWeightInputRef.current?.focus();
      petWeightInputRef.current?.select();
    }
  }, [isPricingModalOpen, isPetWeightEditing]);

  const onClosePricingModal = () => {
    setIsPricingModalOpen(false);
    setShowPetWeightEditorInModal(false);
    setIsPetWeightEditing(false);
  };

  return (
    <section className="relative rounded-[24px] bg-[#F5F5F5] px-[16px] pb-[14px] pt-[16px]">
      {closeNode ? <div className="absolute right-4 top-4 z-10">{closeNode}</div> : null}
      <div className="flex items-center gap-3">
        {isLoading ? (
          <div className={`${skeletonClass} h-6 w-[280px]`} />
        ) : (
          <p className="text-[14px] font-semibold text-[#24282F]">{upgradeTitle}</p>
        )}
        {isLoading ? (
          <div className={`${skeletonClass} h-[44px] w-full rounded-xl lg:max-w-[420px]`} />
        ) : (
          searchNode
        )}
      </div>

      <div className="mt-3 overflow-hidden rounded-[10px] bg-[#EDEDED] p-[1px]">
        <div className="hidden grid-cols-[1.1fr_1fr_1fr_1fr_0.9fr_1fr_1.1fr] gap-4 bg-[#EDEDED] px-4 py-3 text-[12px] font-medium text-[#24282F] sm:grid">
          <p>User</p>
          <p>Name &amp; Specie</p>
          <p>Pet Weight</p>
          <p>Service</p>
          <p>Price</p>
          <p>Time</p>
          <p>Provider</p>
        </div>

        {isLoading ? (
          <div className="mt-[1px] grid grid-cols-1 gap-3 rounded-[10px] bg-white px-4 py-4 sm:grid-cols-[1.1fr_1fr_1fr_1fr_0.9fr_1fr_1.1fr]">
            {Array.from({ length: 7 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <div className={`${skeletonClass} h-4 w-3/4`} />
                <div className={`${skeletonClass} h-3 w-1/2`} />
              </div>
            ))}
          </div>
        ) : (
          <div className="m-[2px] grid grid-cols-1 gap-4 rounded-[10px] bg-white px-[6px] py-[10px] text-[12px] text-[#232932] sm:grid-cols-[1.1fr_1fr_1fr_1fr_0.9fr_1fr_1.1fr]">
            <div>
              <p className="font-medium text-[#2B2E34]">{customerName}</p>
            </div>
            <div>
              <p className="font-medium text-[#2B2E34]">{petName}</p>
              <Species className="" speciesId={pet_species_type_id} />
            </div>
            <div>
              <p className="font-medium text-[#2B2E34]">{normalizedPetWeightDisplay}</p>
              <button
                type="button"
                disabled={!canUpdatePetWeight}
                className={`mt-1 whitespace-nowrap underline ${canUpdatePetWeight ? "text-[#0068FF]" : "cursor-not-allowed text-[#9AA1AF]"
                  }`}
                onClick={onClickUpdatePetWeight}
              >
                Update Pet Weight
              </button>
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="font-medium text-[#2B2E34]">{serviceName}</p>
                {serviceOfferCallout && <OfferCalloutBadge callout={serviceOfferCallout} />}
              </div>
              <p className="mt-1 text-[#6F7685]">{serviceTypeName}</p>
            </div>
            <div>
              <div className="flex items-center gap-1">
                {isFreeFollowUp ? (
                  <span className="font-semibold text-[#239A3D]">FREE</span>
                ) : (
                  <span className="font-medium text-[#2B2E34]">{servicePriceLabel}</span>
                )}
                {
                  hasServiceDiscount && !isFreeFollowUp && (
                    <span className="text-[12px] font-medium text-[#239A3D]">{serviceDiscount}%</span>
                  )
                }
                {!isVaccination && (
                  <button
                    type="button"
                    disabled={!canFetchPricing}
                    className={`rounded-full transition ${canFetchPricing ? "text-[#7E8797] hover:text-[#232932]" : "cursor-not-allowed text-[#B9BEC9]"
                      }`}
                    onClick={() => openPricingModal(false)}
                  >
                    <InformationCircleIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              {isFreeFollowUp ? (
                <p className="mt-1 text-[#6F7685] line-through">{formatAmount(Number(followUpChip?.price_without_follow_up))}</p>
              ) : (
                hasServiceDiscount && <p className="mt-1 text-[#6F7685] line-through">{serviceOriginalPriceLabel}</p>
              )}
              {followUpChip?.text && <p className="mt-1 whitespace-nowrap font-medium text-[#239A3D]">{followUpLabel}</p>}
            </div>
            <div>
              <p className="font-medium text-[#2B2E34]">{slotStartTimeLabel}</p>
              <p className="mt-1 text-[#6F7685]">{bookingDateLabel}</p>
            </div>
            <div>
              <p className="font-medium text-[#2B2E34]">{staffName}</p>
              <p className="mt-1 text-[#6F7685]">{staffExperience}</p>
            </div>
          </div>
        )}
      </div>

      {showHomeServiceMeta && (
        <div className="mt-3 rounded-[10px] border border-[#E4E7ED] bg-white px-4 py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex items-center gap-3">
              <img src={clinicIcon} alt="clinic" className="mt-1 h-4 w-4 shrink-0" />
              <div>
                <p className="text-[12px] font-medium text-[#222222]">Clinic</p>
                <p className="mt-1 text-[12px] text-[#6C727F]">{clinicName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <FaLocationDot className="mt-1 h-4 w-4 shrink-0 text-[#222222]" />
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-[12px] font-medium text-[#222222]">Visit Address </p>
                  <button
                    type="button"
                    className="rounded-[44px] bg-[#EEF2F8] py-[2px] px-[8px] text-[#0068FF]"
                    aria-label="Edit visit address"
                    onClick={onClickEditVisitAddress}
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-1 text-[12px] text-[#6C727F]">{visitAddress}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {isPaidBooking && (
        <p className="mt-2 inline-flex w-fit rounded-[4px] bg-[#FFD8D8] px-[8px] py-[4px] text-[12px] font-medium text-[#ED0101]">
          {bookingFeeLabel} Advance Payment Required
        </p>
      )}

      {isLoading && <>
        <div className="flex mt-2 px-4 items-center gap-[12px]">
          <img src={travelIcon} alt="travel" className="h-3 w-3 shrink-0" />
          <p className="text-[12px] text-[#6C6C6C]">Estimated travel time: {travelTimeLabel}</p>
          <p className=" inline-flex rounded-[4px] bg-[#FFFBED] px-[8px] py-[4px] text-[12px] font-medium text-[#D38000]">
            {waivedOffLabel}
          </p>
          <p
            className="mt-3 inline-flex rounded-md px-3 py-1 text-xs font-medium mb-[14px]"
            style={{
              backgroundColor: chipData?.booking_fee_chip?.bg,
              color: chipData?.booking_fee_chip?.color,
            }}
          >
            {chipData?.booking_fee_chip?.text}
          </p>
        </div>
      </>
      }

      <Transition.Root show={isPricingModalOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-[1300]" onClose={onClosePricingModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto px-4 py-8 sm:px-6">
            <div className="flex min-h-full items-center justify-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-[400px] rounded-[24px] bg-[#FFFFFF] p-4 shadow-[0_16px_60px_rgba(16,24,40,0.32)] sm:p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="pr-2 text-[14px] font-medium leading-tight text-[#222222]">
                      {modalTitle}
                    </h3>
                    <button
                      type="button"
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F5F4F5] p-0 text-[#222222] transition hover:bg-[#DDDDDD]"
                      onClick={onClosePricingModal}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-2 flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <p className="text-[14px] font-medium leading-none text-[#222222]">
                        {formatAmount(currentPriceValue)}
                      </p>
                      {hasCurrentDiscount && (
                        <p className="text-[12px] font-medium text-[#6D6D6D] line-through">
                          {formatAmount(currentOriginalPriceValue)}
                        </p>
                      )}
                      {hasCurrentDiscount && (
                        <p className="text-[12px] font-bold text-[#239A3D]">
                          {currentDiscountPercent}%
                        </p>
                      )}
                    </div>
                    <span className="rounded-[4px] bg-[#F1F0F0] px-[4px] py-[2px] text-[10px] text-[#6D6D6D]">
                      {currentDurationLabel}
                    </span>
                  </div>

                  {showPetWeightEditorInModal && (
                    <div className="mt-2 rounded-[8px] border border-[#E2E2E2] bg-[#FFFFFF] px-2">
                      <div className="flex items-center gap-2">
                        <input
                          ref={petWeightInputRef}
                          value={petWeightInput}
                          onChange={(event) => setPetWeightInput(event.target.value)}
                          readOnly={!isPetWeightEditing}
                          placeholder="Enter pet weight"
                          className={`w-full h-[36px] border-0 bg-transparent text-[12px] font-medium text-[#222222] outline-none focus:border-0 focus:outline-none focus:ring-0 ${isPetWeightEditing ? "cursor-text" : "cursor-default"
                            }`}
                        />
                        {isPetWeightEditing ? (
                          <button
                            type="button"
                            disabled={isPetWeightUpdateInProgress}
                            className="shrink-0 rounded-md bg-[#1E9647] px-2.5 py-1 text-xs font-semibold text-white"
                            onClick={onSavePetWeight}
                          >
                            {isPetWeightUpdateInProgress ? "Saving..." : "Save"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="shrink-0 rounded-md p-1 text-[#0D6EFD] transition"
                            onClick={onEditPetWeight}
                          >
                            <img src={editicon} alt="edit" className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-3 overflow-hidden rounded-[8px] bg-[#F5F4F4] p-[1px]">
                    {isServicePricingInProgress ? (
                      <div className="m-[2px] space-y-2 rounded-[10px] bg-white px-[6px] py-[10px]">
                        {Array.from({ length: 4 }).map((_, index) => (
                          <div key={index} className="h-[56px] animate-pulse rounded-[8px] bg-[#F2F2F2]" />
                        ))}
                      </div>
                    ) : sizePricing === null ? (
                      <div className="m-[2px] rounded-[8px] bg-white px-3 py-5 text-center text-xs font-medium text-[#222222]">
                        One price fits all
                      </div>
                    ) : !sizePricing?.length ? (
                      <div className="m-[2px] rounded-[8px] bg-white px-3 py-5 text-center text-xs text-[#666B73]">
                        No pricing data available
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-[1.1fr_1fr] gap-2 bg-[#F5F4F4] p-[12px] text-[12px] font-medium text-[#222222]">
                          <p>{headerKey}</p>
                          <p className="text-center">{headerVal}</p>
                        </div>
                        <div className="m-[2px] grid grid-cols-1 gap-2 rounded-[8px] bg-white p-[12px]">
                          {sizePricing.map((item) => {
                            const sizeFinalPrice = Number(item.final_price ?? 0);
                            const sizeOriginalPrice = Number(item.price ?? sizeFinalPrice);
                            const sizeDiscountPercent = item.discount_percent ?? 0;
                            const hasSizeDiscount = sizeDiscountPercent > 0;
                            return (
                              <div
                                key={item.id}
                                className="grid grid-cols-[1.1fr_1fr] items-center gap-2"
                              >
                                <p className="text-[14px] text-[#222222]">
                                 {item.size_label}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0. whitespace-nowrap justify-center">
                                  <p className="text-[14px] text-[#222222]">
                                    {formatAmount(sizeFinalPrice)}
                                  </p>
                                  {hasSizeDiscount && (
                                    <p className="text-[12px] text-[#7D8188] line-through">
                                      {formatAmount(sizeOriginalPrice)}
                                    </p>
                                  )}
                                  {hasSizeDiscount && (
                                    <p className="text-[12px] font-medium text-[#239A3D]">
                                      {sizeDiscountPercent}%
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </section>
  );
};

export default ServiceSummaryCard;
